import {
  useDeleteEventResourceMutation,
  useEventRoomSummaryQuery,
  useEventUpcomingRsvpsQuery,
  useSaveEventResourceMutation,
  useSessionMembersQuery,
  useSessionRequestsQuery,
  useSessionSummaryQuery,
} from "@crowdcoursing/api/graphql";
import {
  type UpdateEventRequest,
  useEventAddFacilitator,
  useEventAddRsvp,
  useEventAddToEvent,
  useEventAddToEventByEmail,
  useEventBulkCreateEventTimes,
  useEventCancelRequestAddToEvent,
  useEventChangeOwnership,
  useEventDeleteEvent,
  useEventDeleteFacilitator,
  useEventDeleteMeetingTime,
  useEventDenyRequest,
  useEventLeaveEvent,
  useEventRemoveFromEvent,
  useEventRemoveRsvp,
  useEventRequestAddToEvent,
  useEventUpdateEvent,
} from "@crowdcoursing/api/openapi";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Route } from "~services/router/Route";
import { type MeetingTimeData } from "~ui/organisms/AddEventTimesModal/AddEventTimesModal.types";
import { valueOrFallback } from "~utils/sanitizeDto";

import { useEventAvatar } from "./useEventAvatar";
import { useEventMilestoneManagement } from "./useEventMilestoneManagement";
import { useEventTaskManagement } from "./useEventTaskManagement";
import { useToast } from "./useToast";

// User-facing strings
const STRINGS = {
  CREATED_MULTIPLE: "Created",
  CREATED_SINGLE: "Successfully created",
  DELETE_FAILED: "Failed to delete meeting time. Please try again.",
  DELETE_SUCCESS: "Successfully deleted meeting time",
  FAILED_CREATE: "Failed to create",
  MEETING_TIMES: "meeting times",
  OF: "of",
} as const;

interface UseEventDashboardProps {
  eventId: string;
  userId: string;
}

interface Resource {
  id: string;
  name: string;
  description: string;
  url: string;
}

export const useEventDashboard = ({ eventId, userId }: UseEventDashboardProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [pendingRemovalMember, setPendingRemovalMember] = useState<{ id: string; name: string } | undefined>();
  const [isCreatingMeetingTimes, setIsCreatingMeetingTimes] = useState(false);
  const [isCancelRequestDialogOpen, setIsCancelRequestDialogOpen] = useState(false);

  // Event data
  const {
    data: event,
    isLoading: isEventLoading,
    error: eventError,
    refetch: refetchEvent,
  } = useSessionSummaryQuery(
    { eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId") },
    {
      select: (data) => {
        return data.session;
      },
    },
  );

  // Member data - fetch for everyone
  const {
    data: memberData,
    isLoading: isMemberLoading,
    error: memberError,
    refetch: refetchMemberData,
  } = useSessionMembersQuery(
    { id: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId") },
    {
      enabled: Boolean(event?.id), // Always fetch if we have an event ID
      select: (data) => {
        const facilitators = data.session?.facilitators.map(({ id }) => id) ?? [];
        const owner = data.session?.owner;
        return {
          members:
            data.session?.members.map((member) => ({
              ...member,
              isFacilitator: facilitators.includes(member.id) || member.id === owner?.id,
              isOwner: member.id === owner?.id,
            })) ?? [],
        };
      },
    },
  );

  // Request data - only fetch if user is a facilitator
  const {
    data: requestData,
    isLoading: isRequestLoading,
    error: requestError,
    refetch: refetchRequestData,
  } = useSessionRequestsQuery(
    { id: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId") },
    {
      enabled: Boolean(event?.id && event.userIsFacilitator),
      select: (data) => ({
        requests: data.session?.requests.map((request) => ({ ...request, isVetted: false })) ?? [],
      }),
    },
  );

  // Initialize refetchAll before using it
  const refetchAll = useCallback(() => {
    refetchEvent();
    refetchMemberData();
    refetchRequestData();
  }, [refetchEvent, refetchMemberData, refetchRequestData]);

  // Add member mutation
  const { mutateAsync: addMemberByEmail } = useEventAddToEventByEmail();
  const { mutateAsync: removeFromEvent } = useEventRemoveFromEvent();

  // Member management handlers
  const handleAddMember = useCallback(
    async (member: { firstName: string; lastName: string; email: string }) => {
      await addMemberByEmail(
        {
          body: {
            email: member.email,
            firstName: member.firstName,
            lastName: member.lastName,
          },
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
          },
        },
        {
          onError: (error: unknown) => {
            console.error("Error adding member:", error);
            toast.error("Failed to add member. Please try again.");
          },
          onSuccess: () => {
            toast.success("Member invitation sent successfully!");
            refetchAll(); // Refresh member data
          },
        },
      );
    },
    [addMemberByEmail, eventId, toast, refetchAll],
  );

  // Combine member and request data
  const combinedMemberData = useMemo(
    () => ({
      members: memberData?.members ?? [],
      requests: requestData?.requests ?? [],
    }),
    [memberData?.members, requestData?.requests],
  );

  const handleRemoveMember = useCallback(
    (memberId: string) => {
      if (!memberId) {
        setPendingRemovalMember(undefined);
        return;
      }
      const member = combinedMemberData.members.find((m) => m.id === memberId);
      if (member) {
        setPendingRemovalMember({ id: member.id, name: `${member.firstName} ${member.lastName}` });
      }
    },
    [combinedMemberData.members],
  );

  // Get all RSVPs by using a very wide date range
  const dateRange = useMemo(() => {
    // Get start of today in user's timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneYearFromNow = new Date(today.getTime() + 1 * 365 * 24 * 60 * 60 * 1000);
    return {
      end: oneYearFromNow.toISOString(),
      start: today.toISOString(),
    };
  }, []);

  // RSVP data - only fetch if user is a member or facilitator
  const { data: rsvpData, refetch: refetchRsvps } = useEventUpcomingRsvpsQuery(
    {
      eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
      ...dateRange,
    },
    {
      enabled: Boolean(event?.id && (event.userIsMember || event.userIsFacilitator)),
      select: (data) =>
        data.session?.eventUpcomingRsvps.map((rsvp) => ({
          id: rsvp.id,
          meetingTime: {
            beginMeetingTime: rsvp.meetingTime.beginMeetingTime,
            endMeetingTime: rsvp.meetingTime.endMeetingTime,
            id: rsvp.meetingTime.id,
          },
          user: {
            email: rsvp.user.email,
            firstName: rsvp.user.firstName,
            id: rsvp.user.id,
            lastName: rsvp.user.lastName,
          },
        })) ?? [],
    },
  );

  // Room data (if event is part of a room)
  const { data: roomData } = useEventRoomSummaryQuery(
    { roomId: event?.roomId ?? "" },
    {
      enabled: Boolean(event?.roomId),
      select: (data) => data.room,
    },
  );

  // Event avatar
  const { imageUrl, status, uploadImage } = useEventAvatar(eventId);

  // Resource management
  const { mutate: saveResource } = useSaveEventResourceMutation({ onSuccess: () => refetchEvent() });
  const { mutate: deleteResource } = useDeleteEventResourceMutation({ onSuccess: () => refetchEvent() });

  // Event deletion
  const { mutate: deleteEvent } = useEventDeleteEvent({
    onSuccess: () => {
      if (roomData?.id) {
        navigate(Route.roomView({ roomId: roomData.id }));
      } else {
        navigate(Route.home());
      }
    },
  });

  // Facilitator management
  const { mutateAsync: addFacilitator } = useEventAddFacilitator();
  const { mutateAsync: deleteFacilitator } = useEventDeleteFacilitator();

  // Meeting time creation
  const { mutateAsync: bulkCreateEventTimes } = useEventBulkCreateEventTimes();

  const handleCreateMeetingTimes = useCallback(
    async (meetingTimes: MeetingTimeData[], eventId: string) => {
      console.log("Creating meeting times for eventId:", eventId);
      setIsCreatingMeetingTimes(true);

      await bulkCreateEventTimes(
        {
          body: {
            createEventTimes: meetingTimes.map((meetingTime) => ({
              beginMeetingTime: meetingTime.startTime.toISO() || meetingTime.startTime.toString(),
              endMeetingTime: meetingTime.endTime.toISO() || meetingTime.endTime.toString(),
              eventId,
              maximumSlots: meetingTime.capacity,
              meetingTimeSeriesId: meetingTime.seriesId,
            })),
          },
          pathParams: { eventId },
        },
        {
          onError: (error) => {
            console.error("Failed to create meeting times:", error);
            toast.error(`${STRINGS.FAILED_CREATE} ${meetingTimes.length} ${STRINGS.MEETING_TIMES}`);
          },
          onSettled: () => {
            setIsCreatingMeetingTimes(false);
          },
          onSuccess: async () => {
            const message = `${STRINGS.CREATED_SINGLE} ${meetingTimes.length} ${STRINGS.MEETING_TIMES}!`;
            toast.success(message);
            // Refetch event data to get new meeting times
            await refetchEvent();
          },
        },
      );
    },
    [bulkCreateEventTimes, toast, refetchEvent],
  );

  const { mutateAsync: deleteMeetingTime } = useEventDeleteMeetingTime();

  const handleDeleteMeetingTime = useCallback(
    async (meetingId: string) => {
      await deleteMeetingTime(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            meetingId,
          },
        },
        {
          onError: (error) => {
            console.error("Error deleting meeting time:", error);
            toast.error(STRINGS.DELETE_FAILED);
          },
          onSuccess: async () => {
            toast.success(STRINGS.DELETE_SUCCESS);
            await refetchEvent();
          },
        },
      );
    },
    [deleteMeetingTime, eventId, refetchEvent, toast],
  );

  // Member management mutations
  const { mutateAsync: addToEvent } = useEventAddToEvent();
  const { mutateAsync: denyRequest } = useEventDenyRequest();

  const handleApproveMember = useCallback(
    async (memberId: string) => {
      await addToEvent(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            userId: memberId,
          },
        },
        {
          onError: (error) => {
            console.error("Error approving member:", error);
            toast.error("Failed to approve member request. Please try again.");
          },
          onSuccess: async () => {
            toast.success("Member request approved successfully!");
            // Refetch both member and request data
            await Promise.all([refetchMemberData(), refetchRequestData()]);
          },
        },
      );
    },
    [addToEvent, eventId, refetchMemberData, refetchRequestData, toast],
  );

  const handleRejectMember = useCallback(
    async (memberId: string) => {
      await denyRequest(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            userId: memberId,
          },
        },
        {
          onError: (error) => {
            console.error("Error rejecting member:", error);
            toast.error("Failed to reject member request. Please try again.");
          },
          onSuccess: async () => {
            toast.success("Member request rejected successfully!");
            // Only need to refetch request data since the member was never added
            await refetchRequestData();
          },
        },
      );
    },
    [denyRequest, eventId, refetchRequestData, toast],
  );

  const confirmRemoveMember = useCallback(async () => {
    if (pendingRemovalMember) {
      await removeFromEvent(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            userId: pendingRemovalMember.id,
          },
        },
        {
          onError: (error) => {
            console.error("Error removing member:", error);
            toast.error("Failed to remove member. Please try again.");
          },
          onSettled: () => {
            setPendingRemovalMember(undefined);
          },
          onSuccess: async () => {
            toast.success("Successfully removed member");
            // Refetch member data
            await Promise.all([refetchMemberData(), refetchRequestData()]);
          },
        },
      );
    }
  }, [pendingRemovalMember, eventId, removeFromEvent, toast, refetchMemberData, refetchRequestData]);

  // RSVP management
  const { mutateAsync: addRsvp } = useEventAddRsvp();
  const { mutateAsync: removeRsvp } = useEventRemoveRsvp();

  const handleRsvp = useCallback(
    async (meetingTimeId: string) => {
      await addRsvp(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            meetingId: meetingTimeId,
          },
        },
        {
          onError: (error) => {
            console.error("Error RSVPing:", error);
            toast.error("Failed to RSVP. Please try again.");
          },
          onSuccess: async () => {
            toast.success("Successfully RSVP'd");
            // Refetch both RSVP and event data to update userHasRsvpd flags
            await Promise.all([refetchRsvps(), refetchEvent()]);
          },
        },
      );
    },
    [eventId, addRsvp, refetchRsvps, refetchEvent, toast],
  );

  const handleUnrsvp = useCallback(
    async (meetingTimeId: string) => {
      await removeRsvp(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            meetingId: meetingTimeId,
          },
        },
        {
          onError: (error) => {
            console.error("Error cancelling RSVP:", error);
            toast.error("Failed to cancel RSVP. Please try again.");
          },
          onSuccess: async () => {
            toast.success("Successfully cancelled RSVP");
            // Refetch both RSVP and event data to update userHasRsvpd flags
            await Promise.all([refetchRsvps(), refetchEvent()]);
          },
        },
      );
    },
    [eventId, removeRsvp, refetchRsvps, refetchEvent, toast],
  );

  const handleShareEvent = useCallback(() => {
    // TODO: Implement event sharing
    console.log("Sharing event");
  }, []);

  const handleDeleteEvent = useCallback(() => {
    if (event?.id) {
      deleteEvent({ pathParams: { eventId: event.id } });
    }
  }, [deleteEvent, event?.id]);

  const handleAddFacilitator = useCallback(
    async (memberId: string) => {
      await addFacilitator(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            userId: memberId,
          },
        },
        {
          onError: (error) => {
            console.error("Error promoting to facilitator:", error);
            toast.error("Failed to promote member to facilitator. Please try again.");
          },
          onSuccess: () => {
            toast.success("Member promoted to facilitator successfully!");
            refetchEvent(); // Refresh event data
          },
        },
      );
    },
    [addFacilitator, eventId, refetchEvent, toast],
  );

  const handleRemoveFacilitator = useCallback(
    async (memberId: string) => {
      await deleteFacilitator(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            userId: memberId,
          },
        },
        {
          onError: (error) => {
            console.error("Error demoting facilitator:", error);
            toast.error("Failed to demote facilitator. Please try again.");
          },
          onSuccess: () => {
            toast.success("Facilitator demoted to member successfully!");
            refetchEvent(); // Refresh event data
          },
        },
      );
    },
    [deleteFacilitator, eventId, refetchEvent, toast],
  );

  const { mutateAsync: requestAddToEvent } = useEventRequestAddToEvent();
  const { mutateAsync: leaveEvent } = useEventLeaveEvent();
  const { mutateAsync: cancelEventRequest } = useEventCancelRequestAddToEvent();

  const handleRequestJoin = useCallback(async () => {
    await requestAddToEvent(
      {
        pathParams: {
          eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
          userId: valueOrFallback(userId, "MISSING_USER_ID", "(useEventDashboard) Missing userId"),
        },
      },
      {
        onError: (error) => {
          console.error("Error requesting to join event:", error);
          toast.error("Failed to request to join event. Please try again.");
        },
        onSuccess: async () => {
          toast.success("Successfully requested to join event");
          await refetchEvent();
        },
      },
    );
  }, [eventId, userId, requestAddToEvent, toast, refetchEvent]);

  const handleLeave = useCallback(async () => {
    await leaveEvent(
      {
        pathParams: {
          eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
        },
      },
      {
        onError: (error) => {
          console.error("Error leaving event:", error);
          toast.error("Failed to leave event. Please try again.");
        },
        onSuccess: async () => {
          toast.success("Successfully left event");
          await refetchEvent();
        },
      },
    );
  }, [eventId, leaveEvent, toast, refetchEvent]);

  const handleCancelRequest = useCallback(() => {
    setIsCancelRequestDialogOpen(true);
  }, []);

  const confirmCancelRequest = useCallback(async () => {
    await cancelEventRequest(
      {
        pathParams: {
          eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
          userId: valueOrFallback(userId, "MISSING_USER_ID", "(useEventDashboard) Missing userId"),
        },
      },
      {
        onError: (error) => {
          console.error("Error canceling event request:", error);
          toast.error("Failed to cancel request. Please try again.");
        },
        onSuccess: async () => {
          await refetchEvent();
        },
      },
    );
  }, [eventId, userId, cancelEventRequest, toast, refetchEvent]);

  // Owner management
  const { mutateAsync: changeOwnership } = useEventChangeOwnership();

  const handleAddOwner = useCallback(
    async (memberId: string) => {
      await changeOwnership(
        {
          pathParams: {
            eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
            userId: memberId,
          },
        },
        {
          onError: (error) => {
            console.error("Error changing owner:", error);
            toast.error("Failed to change owner. Please try again.");
          },
          onSuccess: async () => {
            toast.success("Owner changed successfully!");
            // Refetch both member and request data since ownership affects roles
            await Promise.all([refetchMemberData(), refetchRequestData()]);
          },
        },
      );
    },
    [changeOwnership, eventId, refetchMemberData, refetchRequestData, toast],
  );

  // Event update
  const { mutateAsync: updateEvent } = useEventUpdateEvent();

  const handleUpdateEvent = useCallback(
    async (event: {
      bubbleId: string;
      description: string;
      isAutoJoin: boolean;
      isSearchable: boolean;
      name: string;
    }) => {
      const req: UpdateEventRequest = {
        bubbleId: event.bubbleId,
        description: event.description,
        eventId: valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
        isPublic: event.isAutoJoin,
        isSearchable: event.isSearchable,
        name: event.name,
      };

      await updateEvent(
        { body: req, pathParams: { eventId } },
        {
          onError: (error) => {
            console.error("Error updating event:", error);
            toast.error("Failed to update event. Please try again.");
          },
          onSuccess: async () => {
            toast.success("Event updated successfully!");
            await refetchEvent();
          },
        },
      );
    },
    [eventId, updateEvent, toast, refetchEvent],
  );

  // Initialize management hooks
  const resourceManagement = {
    deleteResource: (resource: Resource) => deleteResource({ request: { eventId, id: resource.id } }),
    saveResource: (newResource: Omit<Resource, "id">, oldResource?: Resource) =>
      saveResource({ request: { eventId, id: oldResource?.id, ...newResource } }),
  };

  const taskManagement = useEventTaskManagement(
    valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
    valueOrFallback(userId, "MISSING_USER_ID", "(useEventDashboard) Missing userId"),
    refetchAll,
  );

  const milestoneManagement = useEventMilestoneManagement(
    valueOrFallback(eventId, "MISSING_EVENT_ID", "(useEventDashboard) Missing eventId"),
    refetchAll,
  );

  return {
    chatMembers: combinedMemberData.members ?? [],
    confirmCancelRequest,
    confirmRemoveMember,
    error: eventError || memberError || requestError,
    event,
    handleAddFacilitator,
    handleAddMember,
    handleAddMilestone: milestoneManagement.createMilestone,
    handleAddOwner,
    handleAddTask: taskManagement.createTask,
    handleApproveMember,
    handleCancelRequest,
    handleCompleteMilestone: milestoneManagement.completeMilestone,
    handleCompleteTask: taskManagement.completeTask,
    handleCreateMeetingTimes,
    handleDeleteEvent,
    handleDeleteMeetingTime,
    handleDeleteMilestone: milestoneManagement.deleteMilestone,
    handleDeleteTask: taskManagement.deleteTask,
    handleLeave,
    handleRejectMember,
    handleRemoveFacilitator,
    handleRemoveMember,
    handleRequestJoin,
    handleRsvp,
    handleShareEvent,
    handleUnrsvp,
    handleUpdateEvent,
    imageUrl,
    isCancelRequestDialogOpen,
    isCreatingMeetingTimes,
    isLoading: isEventLoading || isMemberLoading || isRequestLoading,
    memberData: combinedMemberData,
    pendingRemovalMember,
    refetchEvent,
    resourceManagement,
    rsvpData,
    setIsCancelRequestDialogOpen,
    setPendingRemovalMember,
    status,
    uploadImage,
  };
};
